// ============================================================================
// THE ELAPSED TIMER, WHERE THE USER IS ACTUALLY LOOKING
// ============================================================================
//
// The elapsed count used to sit beside the Stop button in the composer. During
// a long turn nobody watches the Stop button: they watch the tool list grow.
// So the count belongs on the live status line at the end of that list, where
// it reads against the activity it is timing ("Creating - 0:42") instead of
// against a control the user is trying not to press.
//
// IT TICKS ON ITS OWN. The obvious alternative is to let the parent recompute
// the elapsed seconds on its own render cadence, and that quietly fails: the
// streaming heartbeat that re-renders the reply is suppressed for private and
// licensed-data sessions, so the number would freeze on exactly the turns that
// run longest. One interval here, owned by the only component that needs it,
// keeps the tick independent of how often the transcript re-renders.
//
// Not conditional on which harness ran the turn. Claude and opencode turns get
// the same chrome, for the same reason the tool cards do.

import React from 'react';
import { Typography } from '@mui/material';
import { formatDurationLabel } from '../utils';

// Matches the cap MessageRenderer applies to a resolved duration: an abandoned
// or stale session must not render as "52 hours".
// An absurdity guard, not a limit on how long work may take, and the same
// threshold MessageRenderer and the todo card use.
//
// It was 10 minutes, which sits inside the range of ordinary work here: a
// workbook build routinely runs longer, so the number froze at "10m" while the
// turn was still going. A frozen clock beside a live Stop button is the one
// state that tells a user nothing at all.
//
// It cannot simply be removed. This path can be handed a startTime from a
// restored message that still claims to be streaming, and then it would render
// 26 hours. Six hours is past any real turn and still catches that. Reaching it
// clamps the NUMBER only: whether a turn is streaming is the turn's business,
// never the clock's.
const MAX_DISPLAY_DURATION_SECONDS = 6 * 60 * 60;

const parseStart = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
};

/**
 * Seconds between `startTime` and either `endTime` or now, floored and capped.
 *
 * A missing or unparseable start falls back to the seconds the caller already
 * resolved, so the label degrades to a static number rather than to zero.
 *
 * `endTime` is what makes a finished turn static BY CONSTRUCTION. Without it
 * the count is only ever static by accident: it is `now - start` recomputed
 * every second, so it keeps climbing for as long as the component is mounted,
 * no matter what the surrounding flags claim about the turn being over.
 */
export const elapsedSecondsFrom = (startTime, fallbackSeconds = 0, nowMs = Date.now(), endTime = null) => {
    const startMs = parseStart(startTime);
    const endMs = parseStart(endTime);
    const raw = startMs === null
        ? (Number.isFinite(fallbackSeconds) ? fallbackSeconds : 0)
        : Math.floor(((endMs === null ? nowMs : endMs) - startMs) / 1000);
    return Math.min(Math.max(0, raw), MAX_DISPLAY_DURATION_SECONDS);
};

/**
 * The elapsed count for a turn: live while it runs, frozen once it ends.
 *
 * @param {Object} props
 * @param {string|number|null} props.startTime when the turn started
 * @param {string|number|null} props.endTime when the turn settled, if it has
 * @param {boolean} props.running whether to keep counting
 * @param {number} props.fallbackSeconds used when there is no usable start
 * @param {Object} props.sx style overrides, so the caller owns the typography
 */
const StreamElapsedTimer = ({
    startTime = null,
    endTime = null,
    running = true,
    fallbackSeconds = 0,
    sx = {},
}) => {
    // A settled turn has an end, and an end outranks `running`. The flags that
    // drive `running` live on two separate objects (the timer component and
    // the message metadata) and either can lag a terminal event, so the timer
    // does not trust them when it holds the end timestamp itself.
    const isSettled = parseStart(endTime) !== null;

    const [seconds, setSeconds] = React.useState(
        () => elapsedSecondsFrom(startTime, fallbackSeconds, Date.now(), endTime)
    );

    React.useEffect(() => {
        // The updater bails out when the second has not turned over, so a
        // one-per-second interval costs at most one render per second.
        const sync = () => setSeconds((previous) => {
            const next = elapsedSecondsFrom(startTime, fallbackSeconds, Date.now(), endTime);
            return next === previous ? previous : next;
        });
        sync();
        if (!running || isSettled) return undefined;
        const interval = setInterval(sync, 1000);
        return () => clearInterval(interval);
        // `fallbackSeconds` is deliberately not a dependency: it moves every
        // time the parent re-resolves the duration, and restarting the interval
        // on each of those would make the tick stutter. It is only read when
        // there is no usable start time, and that does not change mid turn.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startTime, endTime, running, isSettled]);

    return (
        <Typography
            component="span"
            variant="caption"
            role="timer"
            aria-label={isSettled
                ? `Request took ${seconds} seconds`
                : `Request running for ${seconds} seconds`}
            sx={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                color: 'inherit',
                ...sx,
            }}
        >
            {formatDurationLabel(seconds)}
        </Typography>
    );
};

export default StreamElapsedTimer;
